import { useImpersonateWorkspace } from '../../workspace/hooks/useImpersonateWorkspace';
import { useAccessTokenReceived } from './useAccessTokenReceived';
import { useCurrentWorkspace } from './useCurrentWorkspace';
import { ImpersonationDraft } from './useImpersonationDraft';
import { useImpersonateUser } from './useImpersonateUser';
import { useIsInImpersonateUser } from './useIsInImpersonateUser';
import { useUnsyncedWorkGuard } from '../../offline/useUnsyncedWorkGuard';

export const useApplyImpersonation = () => {
  const isInImpersonateUser = useIsInImpersonateUser();
  const currentWorkspace = useCurrentWorkspace(true);
  const { impersonateWorkspace } = useImpersonateWorkspace();
  const { impersonateUserAsync } = useImpersonateUser();
  const onAccessTokenReceived = useAccessTokenReceived();
  const confirmLeave = useUnsyncedWorkGuard();

  /** Both resolve false, with the session untouched, when the user chose to keep their unsynced work. */
  const cancel = async (): Promise<boolean> => {
    if (isInImpersonateUser) {
      // Asked here, before anything changes: the swap below would otherwise
      // have to be abandoned half way through.
      if (!(await confirmLeave())) return false;
      await onAccessTokenReceived('', true, false);
    }
    if (currentWorkspace) {
      await impersonateWorkspace(undefined);
    }
    return true;
  };

  const apply = async (draft: ImpersonationDraft): Promise<boolean> => {
    // Entering as another user swaps the token; a workspace-only choice does not.
    if (draft.userId && !(await confirmLeave())) return false;
    if (!(await cancel())) return false;

    if (draft.userId) {
      await impersonateUserAsync({ accountId: draft.userId }, () => null);
      return true;
    }

    if (draft.workspaceId !== undefined) {
      await impersonateWorkspace({
        id: draft.workspaceId,
        name: draft.workspaceName ?? '',
        type: draft.workspaceType,
      });
    }
    return true;
  };

  return { apply, cancel };
};
